/**
 * The one error path.
 *
 * Every non-2xx response from the service carries the same body — `{code, message, context}`
 * with a stable machine-readable `code` (`hismaic/api/errors.py`). The UI branches on `code`
 * and shows `message` verbatim; it never parses `message`, because that string is written for
 * a human and may be reworded without breaking a client.
 *
 * `context` is `dict[str, str]` server-side and is guaranteed to contain no credential
 * material, no presigned URL and no traceback. It is safe to render.
 */

export const ERROR_CODES = [
  'not_found',
  'validation_failed',
  'not_authenticated',
  'forbidden',
  'invite_invalid',
  'email_already_registered',
  'siglum_ambiguous',
  'image_locked',
  'lock_release_not_confirmed',
  'annotator_kind_conflict',
  'image_not_in_s3',
  'image_not_annotatable',
  'invariant_violation',
  'provenance_violation',
  'reading_order_violation',
  'disagreement_conflict',
  'unknown_letter_class',
  's3_not_configured',
  's3_unavailable',
  'export_not_built',
  'export_invalid',
  'vocab_empty',
  'schema_mismatch',
  'not_implemented',
  'internal_error',
  // Client-side only: the request never reached the service.
  'network_error',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ProblemBody {
  code: ErrorCode;
  message: string;
  context: Record<string, string>;
}

export class ApiProblem extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly context: Record<string, string>;

  constructor(body: ProblemBody, status: number) {
    super(body.message);
    this.name = 'ApiProblem';
    this.code = body.code;
    this.status = status;
    this.context = body.context ?? {};
  }

  /**
   * Is retrying this request plausibly useful?
   *
   * `image_locked` is emphatically **not** retryable: another annotator holds the image and
   * hammering the endpoint would neither help us nor them. The autosave loop stops on it and
   * says so rather than fighting.
   */
  get retryable(): boolean {
    switch (this.code) {
      case 'network_error':
      case 's3_unavailable':
      case 'internal_error':
        return true;
      default:
        return this.status >= 500;
    }
  }

  /** A failure the operator has to fix in configuration before anything will work. */
  get fatalForSession(): boolean {
    return this.code === 'schema_mismatch' || this.code === 'vocab_empty';
  }

  /** Who holds the lock, when this is an `image_locked` problem. */
  get lockHolder(): string | null {
    return this.context['locked_by_name'] || this.context['locked_by'] || null;
  }

  get lockExpiresAt(): string | null {
    return this.context['expires_at'] || null;
  }
}

function isProblemBody(value: unknown): value is ProblemBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string' &&
    typeof (value as { message?: unknown }).message === 'string'
  );
}

/** Turn any failed response into an `ApiProblem`, whatever shape the body turned out to be. */
export async function problemFromResponse(response: Response): Promise<ApiProblem> {
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  if (isProblemBody(parsed)) {
    return new ApiProblem({ ...parsed, context: parsed.context ?? {} }, response.status);
  }
  // A body that is not a Problem means something in front of the service answered — a proxy,
  // or a dev server with no backend behind it. Say that, rather than inventing a code.
  return new ApiProblem(
    {
      code: response.status >= 500 ? 'internal_error' : 'validation_failed',
      message: `the service returned HTTP ${response.status} with a body this client does not recognise`,
      context: { http_status: String(response.status) },
    },
    response.status,
  );
}

export function networkProblem(error: unknown): ApiProblem {
  const detail = error instanceof Error ? error.message : String(error);
  return new ApiProblem(
    {
      code: 'network_error',
      message: `could not reach the annotation service: ${detail}`,
      context: {},
    },
    0,
  );
}

export function asProblem(error: unknown): ApiProblem {
  return error instanceof ApiProblem ? error : networkProblem(error);
}
